import { ChargeWaiverEntity, EMPTY_CHARGE_WAIVER_DETAIL_ENTITY } from "domain/entity/ChargeWaiver/ChargeWaiverEntity";
import { ChargeWaiverSearchCriteria, EMPTY_CHARGE_WAIVER_SEARCH_CRITERIA } from "domain/entity/ChargeWaiver/ChargeWaiverSearchCriteria";
import { ResponseEntity } from "domain/entity/Common/ResponseEntity";
import { MasterDataType } from "domain/entity/MasterData/MasterDataEntity";
import { ChargeWaiverRepository } from "domain/repository/ChargeWaiver/ChargeWaiverRepo";
import { MasterDataRepository } from "domain/repository/MasterData/MasterDataRepo";
import { ParameterDetailRepository } from "domain/repository/parameter/ParameterDetailRepo";
import _ from "lodash";
import { ParameterCodeValue } from "presentation/constant/Parameter/ParameterCodeValue";
import { ChargeWaiverModel } from "presentation/model/ChargeWaiver/ChargeWaiverModel";
import { DropdownProps } from "presentation/model/DropdownProps";
import { Dispatch, SetStateAction } from "react";
import { IFieldValue } from "veronica-ui-component/dist/component/core";
import BaseViewModel from "../BaseViewModel";

interface ChargeWaiverVMProps extends BaseViewModel {
    dispatch: [
        Dispatch<SetStateAction<ChargeWaiverModel>> | ((value: SetStateAction<ChargeWaiverModel>) => void),
    ],
    chargeWaiverRepo: ChargeWaiverRepository,
    parameterDetailRepo: ParameterDetailRepository,
    masterDataRepo: MasterDataRepository,
}

export const ChargeWaiverVM = ({ dispatch, chargeWaiverRepo, parameterDetailRepo, masterDataRepo }: ChargeWaiverVMProps) => {
    const [chargeWaiverDispatch] = dispatch;

    const loadDropdownOption = async () => {

        await parameterDetailRepo.getAllParameterDtlsByParameterCode(ParameterCodeValue.WAIVER_REASON_CODE).then(
            parDtls => {
                if (parDtls) {
                    let newParDtls = _.orderBy(parDtls, ["parameterDtlCode"]);
                    const waiverReasonCodeDropdownOptions = newParDtls?.map((parDtl) => ({
                        dropdownLabel: parDtl.parameterDtlCode,
                        tagLabel: parDtl.parameterDtlCode,
                        value: parDtl.parameterDtlCode,
                    })) ?? []

                    chargeWaiverDispatch(prevState => ({
                        ...prevState,
                        dynamicOptions: {
                            ...prevState.dynamicOptions,
                            waiverReasonDropdownOptions: waiverReasonCodeDropdownOptions
                        }
                    }))
                }
            }
        )

        await parameterDetailRepo.getAllParameterDtlsByParameterCode(ParameterCodeValue.WAIVER_REJECT_REASON_CODE).then(
            parDtls => {
                if (parDtls) {
                    let newParDtls = _.orderBy(parDtls, ["parameterDtlCode"]);
                    const rejectReasonCodeDropdownOptions = newParDtls?.map((parDtl) => ({
                        dropdownLabel: parDtl.parameterDtlCode,
                        tagLabel: parDtl.parameterDtlCode,
                        value: parDtl.parameterDtlCode,
                    })) ?? []

                    chargeWaiverDispatch(prevState => ({
                        ...prevState,
                        dynamicOptions: {
                            ...prevState.dynamicOptions,
                            rejectReasonDropdownOptions: rejectReasonCodeDropdownOptions
                        }
                    }))
                }
            }
        )

        await parameterDetailRepo.getAllParameterDtlsByParameterCode(ParameterCodeValue.WAIVER_REVERSE_REASON_CODE).then(
            parDtls => {
                if (parDtls) {
                    let newParDtls = _.orderBy(parDtls, ["parameterDtlCode"]);
                    const reverseReasonCodeDropdownOptions = newParDtls?.map((parDtl) => ({
                        dropdownLabel: parDtl.parameterDtlCode,
                        tagLabel: parDtl.parameterDtlCode,
                        value: parDtl.parameterDtlCode,
                    })) ?? []

                    chargeWaiverDispatch(prevState => ({
                        ...prevState,
                        dynamicOptions: {
                            ...prevState.dynamicOptions,
                            reverseReasonDropdownOptions: reverseReasonCodeDropdownOptions
                        }
                    }))
                }
            }
        )

        await masterDataRepo.getMasterDataByKey(MasterDataType.CONSORTIUM).then(
            consortiumCodes => {
                let consortiumCodeDropdownOptions = consortiumCodes?.map((cons) => ({
                    dropdownLabel: cons.code,
                    tagLabel: cons.code,
                    value: cons.code,
                })) ?? []
                consortiumCodeDropdownOptions = _.orderBy(consortiumCodeDropdownOptions, "dropdownLabel")
                chargeWaiverDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        consortiumCodeDropdownOptions: consortiumCodeDropdownOptions
                    }
                }))
            }
        )

    }

    const searchChargeWaiverData = async (searchCriteria: ChargeWaiverSearchCriteria) => {
        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                chargeWaiverList: [],
                searchCriteria: searchCriteria,
                isShowBulkEditPanel: false,
                isShowEditPanel: false,
                isShowDelayDelivery: false,
                isShowStackHold: false,
                isShowDelayGrding: false,
                isShowRolloverChg: false,
                isShowGenWaiverByCom: false,
                isSubmit: false,
                isSubmitCount: 0,
                editData: { ...EMPTY_CHARGE_WAIVER_DETAIL_ENTITY }
            }
        })
        await chargeWaiverRepo.searchChargeWaiverData(searchCriteria).then((data) => {
            if (data && data.length > 0) {
                chargeWaiverDispatch(prevState => {
                    return {
                        ...prevState,
                        chargeWaiverList: data,
                        selectedRows: [],
                        searchCriteria: searchCriteria,
                        isSubmit: true,
                        isSubmitCount: data.length
                    }
                })
            }
        }).catch((error) => {
            return [];
        })

        await chargeWaiverRepo.searchChargeWaiverApprovalData(searchCriteria).then((data) => {
            if (data && data.length > 0) {
                chargeWaiverDispatch(prevState => {
                    return {
                        ...prevState,
                        isApproval: false,
                        isApprovalCount: data.length
                    }
                })
            } else {
                chargeWaiverDispatch(prevState => {
                    return {
                        ...prevState,
                        isApproval: false,
                        isApprovalCount: 0
                    }
                })
            }
        }).catch((error) => {
            return [];
        })
    }

    const searchChargeWaiverSubmitData = async (searchCriteria: ChargeWaiverSearchCriteria) => {
        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                chargeWaiverList: [],
                searchCriteria: searchCriteria,
                isShowBulkEditPanel: false,
                isShowEditPanel: false,
                isShowDelayDelivery: false,
                isShowStackHold: false,
                isShowDelayGrding: false,
                isShowRolloverChg: false,
                isShowGenWaiverByCom: false,
                editData: { ...EMPTY_CHARGE_WAIVER_DETAIL_ENTITY }
            }
        })
        await chargeWaiverRepo.searchChargeWaiverData(searchCriteria).then((data) => {
            if (data && data.length > 0) {
                chargeWaiverDispatch(prevState => {
                    return {
                        ...prevState,
                        chargeWaiverList: data,
                        selectedRows: [],
                        searchCriteria: searchCriteria,
                        isSubmit: true,
                        isSubmitCount: data.length
                    }
                })
            }
        }).catch((error) => {
            return [];
        })
    }

    const searchChargeWaiverApprovalData = async (searchCriteria: ChargeWaiverSearchCriteria) => {
        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                chargeWaiverList: [],
                searchCriteria: searchCriteria,
                isShowBulkEditPanel: false,
                isShowEditPanel: false,
                isShowDelayDelivery: false,
                isShowStackHold: false,
                isShowDelayGrding: false,
                isShowRolloverChg: false,
                isShowGenWaiverByCom: false,
                editData: { ...EMPTY_CHARGE_WAIVER_DETAIL_ENTITY },
                isApproval: false,
                isApprovalCount: 0,
            }
        })
        await chargeWaiverRepo.searchChargeWaiverApprovalData(searchCriteria).then((data) => {
            if (data && data.length > 0) {
                chargeWaiverDispatch(prevState => {
                    return {
                        ...prevState,
                        chargeWaiverList: data,
                        selectedRows: [],
                        isApproval: true,
                        isApprovalCount: data.length,
                    }
                })
            }
        }).catch((error) => {
            return [];
        })
    }

    const onRemoveSearchCriteria = (fieldName: string, isSearch: boolean = true) => {
        let resetSearchCriteriaPerField = { [fieldName]: EMPTY_CHARGE_WAIVER_SEARCH_CRITERIA[fieldName] }

        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                searchCounter: isSearch ? prevState.searchCounter + 1 : prevState.searchCounter,
                searchCriteria: { ...prevState.searchCriteria, ...resetSearchCriteriaPerField }
            }
        })
    }

    const onRemoveAllSearchCriteria = () => {
        chargeWaiverDispatch(prevState => {
            prevState.currentTableRef?.current?.api.setServerSideDatasource({
                getRows: (params: any) => {
                    params.success({
                        rowData: [],
                        rowCount: 0
                    })
                },
            })

            return {
                ...prevState, searchCounter: 0, searchCriteria: { ...EMPTY_CHARGE_WAIVER_SEARCH_CRITERIA }
            }
        })
    }

    const onMultipleDropdownChange = (e: any, fieldName: string) => {
        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                searchCriteria: {
                    ...prevState.searchCriteria,
                    [fieldName]: _.uniq(e?.map((item: DropdownProps) => item.value)),
                }
            }
        });
    };

    const onDateRangeChange = (startDate: any, endDate: any, dateFields: { startField: string, endField: string }) => {
        chargeWaiverDispatch(prevState => ({
            ...prevState,
            editData: {
                ...prevState.editData,
                [dateFields.startField]: startDate,
                [dateFields.endField]: endDate,
            }
        }))
    }

    const onCloseScreen = () => {
        document.dispatchEvent(new CustomEvent('closeChargeWaiver'))
    }

    const onShowLoading = () => {
        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                isLoading: true,
            }
        })
    }

    const onHideLoading = () => {
        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                isLoading: false,
            }
        })
    }

    const onSearchClick = async () => {
        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                isShowCriteriaPanel: !prevState.isShowCriteriaPanel
            }
        });
    }

    const onReverseClick = async () => {
        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                isShowReversePanel: true,
                isShowRejectPanel: false,
                isShowCriteriaPanel: false,
            }
        });
    }

    const onRejectClick = async () => {
        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                isShowRejectPanel: true,
                isShowReversePanel: false,
                isShowCriteriaPanel: false,
            }
        });
    }

    const onSubmit = async (selectedDocPrevDatas: ChargeWaiverEntity[]): Promise<ResponseEntity> => {
        let idList: number[] = [];
        selectedDocPrevDatas.forEach(item => {
            if (item.id) {
                idList.push(item.id);
            }
        });
        return await chargeWaiverRepo.submit(idList);
    };

    const onReject = async (selectedDocPrevDatas: ChargeWaiverEntity[], reason: string): Promise<ResponseEntity> => {
        let idList: number[] = [];
        selectedDocPrevDatas.forEach(item => {
            if (item.id) {
                idList.push(item.id);
            }
        });
        return await chargeWaiverRepo.reject({ idList, reason });
    };

    const onApprove = async (selectedDocPrevDatas: ChargeWaiverEntity[]): Promise<ResponseEntity> => {
        let idList: number[] = [];
        selectedDocPrevDatas.forEach(item => {
            if (item.id) {
                idList.push(item.id);
            }
        });
        return await chargeWaiverRepo.approve(idList);
    };

    const onReverse = async (selectedDocPrevDatas: ChargeWaiverEntity[], reason: string): Promise<ResponseEntity> => {
        let idList: number[] = [];
        selectedDocPrevDatas.forEach(item => {
            if (item.id) {
                idList.push(item.id);
            }
        });
        return await chargeWaiverRepo.reverse({ idList, reason });
    };

    const onSearchFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        const cntrNoArray = val?.toString().toUpperCase().split('\n');

        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }

        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                searchCriteria: {
                    ...prevState.searchCriteria,
                    [fieldKey]: fieldKey === 'cntrList' ? cntrNoArray : val,
                }
            }
        })
    }

    const onEditFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }
        if (fieldKey === 'reasonCode') {
            chargeWaiverDispatch(prevState => {
                return {
                    ...prevState,
                    reasonCode: val
                }
            })
        } else {
            chargeWaiverDispatch(prevState => {
                return {
                    ...prevState,
                    editData: {
                        ...prevState.editData,
                        [fieldKey]: val,
                    },
                    masterState: {
                        ...prevState.masterState,
                        allFormState: {
                            ...prevState.masterState.allFormState,
                            [fieldKey]: ''
                        },
                    }
                }
            })
        }

    }

    const updateSelectedRows = async (rows: any[]) => {
        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                selectedRows: rows,
                currentSelectedData: rows.length > 0 ? rows[0] : null,
                forceRefresh: !prevState.forceRefresh
            }
        })
    }

    const onSearchCriteriaResetClick = () => {
        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                searchCriteria: EMPTY_CHARGE_WAIVER_SEARCH_CRITERIA
            }
        })
    }

    const onRowDoubleClick = (waiver: ChargeWaiverEntity) => {
        let isShowDelayDelivery = false;
        let isShowStackHold = false;
        let isShowDelayGrding = false;
        let isShowRolloverChg = false;
        let isShowGenWaiverByCom = false;
        if (waiver.waiverReason === 'DELAY_DELIVERY') {
            isShowDelayDelivery = true;
        } else if (waiver.waiverReason === 'DELAY_GROUNDING') {
            isShowDelayGrding = true;
        } else if (waiver.waiverReason === 'GENERAL_WAIVER') {
            isShowGenWaiverByCom = true;
        } else if (waiver.waiverReason === 'ROLLOVER') {
            isShowRolloverChg = true;
        } else if (waiver.waiverReason === 'STUCK_MISSING_STACK_HOLD') {
            isShowStackHold = true;
        }
        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                isShowEditPanel: true,
                isShowBulkEditPanel: false,
                isShowDelayDelivery: isShowDelayDelivery,
                isShowStackHold: isShowStackHold,
                isShowDelayGrding: isShowDelayGrding,
                isShowRolloverChg: isShowRolloverChg,
                isShowGenWaiverByCom: isShowGenWaiverByCom,
                currentSelectedData: waiver,
                editData: waiver,
            }
        })
    }

    const onBulkEditClick = (waiver: ChargeWaiverEntity) => {
        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                isShowBulkEditPanel: true,
                isShowEditPanel: false,
                isShowDelayDelivery: false,
                isShowStackHold: false,
                isShowDelayGrding: false,
                isShowRolloverChg: false,
                isShowGenWaiverByCom: false,
                currentSelectedData: waiver,
                editData: waiver,
            }
        })
    }

    const onEditCloseClick = () => {
        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                isShowEditPanel: false,
                isShowBulkEditPanel: false,
                isShowDelayDelivery: false,
                isShowStackHold: false,
                isShowDelayGrding: false,
                isShowRolloverChg: false,
                isShowGenWaiverByCom: false,
                editData: { ...EMPTY_CHARGE_WAIVER_DETAIL_ENTITY },
                masterState: {
                    ...prevState.masterState,
                    isSaveClicked: false,
                    allFormState: {}
                }
            }
        });
    }

    const onReasonCloseClick = () => {
        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                isShowRejectPanel: false,
                isShowReversePanel: false,
                reasonCode: null,
            }
        });
    }

    const onAdjCntrCloseClick = () => {
        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                isShowAdjCntrPanel: false,
            }
        });
    }

    const onSaveClicked = () => {
        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                masterState: {
                    ...prevState.masterState,
                    isSaveClicked: true,
                    allFormState: {},
                }
            }
        })
    }

    const onEditSaveClick = async (editDatas: ChargeWaiverEntity[], isShowBulkEditPanel: boolean) => {
        if (isShowBulkEditPanel) {
            return await chargeWaiverRepo.bulkUpdate(editDatas);
        } else {
            let validatedResult: { [x: string]: string } = {};
            let resultMap: { [key: string]: string } = {};
            await chargeWaiverRepo.update(editDatas[0]).then(res => {
                if (res && !res.success) {
                    resultMap = { ...res };
                }
            });

            if (Object.keys(resultMap).length > 0) {
                Object.keys(resultMap).forEach(key => {
                    validatedResult[key] = resultMap[key];
                });
                chargeWaiverDispatch(prevState => {
                    return {
                        ...prevState,
                        masterState: {
                            ...prevState.masterState,
                            allFormState: {
                                ...validatedResult
                            },
                        }
                    }
                });

                const res: ResponseEntity = {
                    code: "",
                    success: false,
                    msg: "ERROR",
                    data: validatedResult
                }

                return res;
            } else {
                const res: ResponseEntity = {
                    code: "",
                    success: true,
                    msg: "",
                    data: null
                }

                return res;
            }

        }
    }

    const closeConfirmModal = () => {
        chargeWaiverDispatch(prevState => ({
            ...prevState,
            isShowPrintPad: false,
            isPrinting: true,
        }))
    };

    const cancelConfirmModal = () => {
        chargeWaiverDispatch(prevState => ({
            ...prevState,
            isShowPrintPad: false,
            isPrinting: false,
        }))
    };
    const cancelEditModal = () => {
        chargeWaiverDispatch(prevState => ({
            ...prevState,
            isShowAdjCntrPanel: false,
            docChargeDatas: [],
            selectDocChargeDataRows: []
        }))
    };

    const updateSelectedDocChargeDataRows = async (rows: any[]) => {
        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                selectDocChargeDataRows: rows,
                editForceRefresh: !prevState.editForceRefresh
            }
        })
    }

    const cancelApproveRejectReasonModal = () => {
        return chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                showApproveRejectPanel: '',
                approveRejactReasonCode: ''
            }
        });
    }

    const onApproveRejectReasonChange = (fieldValue: IFieldValue) => {
        let val: any = fieldValue;
        return chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                approveRejactReasonCode: val
            }
        });
    }

    const onGroupButtonClick = async (buttonId: string, searchCriteria: ChargeWaiverSearchCriteria) => {
        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                selectedRows: [],
                chargeWaiverList: [],
                isApproval: false,
                isSubmit: false,
            }
        })
        if (buttonId === "isSubmit") {
            await searchChargeWaiverSubmitData(searchCriteria);
        } else if (buttonId === "isApproval") {
            await searchChargeWaiverApprovalData(searchCriteria);
        } else {
            await searchChargeWaiverData(searchCriteria);
        }
    }

    const setIsSearchLoading = (isSearchLoading: boolean) => {
        chargeWaiverDispatch(prevState => {
            return {
                ...prevState,
                isSearchLoading: isSearchLoading,
            }
        })
    }

    return {
        onApproveRejectReasonChange: onApproveRejectReasonChange,
        cancelApproveRejectReasonModal: cancelApproveRejectReasonModal,
        updateSelectedDocChargeDataRows: updateSelectedDocChargeDataRows,
        cancelEditModal: cancelEditModal,
        searchChargeWaiverData: searchChargeWaiverData,
        loadDropdownOption: loadDropdownOption,
        onRemoveSearchCriteria: onRemoveSearchCriteria,
        onRemoveAllSearchCriteria: onRemoveAllSearchCriteria,
        onMultipleDropdownChange: onMultipleDropdownChange,
        onDateRangeChange: onDateRangeChange,
        onSearchClick: onSearchClick,
        onCloseScreen: onCloseScreen,
        onShowLoading: onShowLoading,
        onHideLoading: onHideLoading,
        onReject: onReject,
        onSearchFieldChange: onSearchFieldChange,
        updateSelectedRows: updateSelectedRows,
        onSearchCriteriaResetClick: onSearchCriteriaResetClick,
        onRowDoubleClick: onRowDoubleClick,
        onEditCloseClick: onEditCloseClick,
        onEditSaveClick: onEditSaveClick,
        onAdjCntrCloseClick: onAdjCntrCloseClick,
        closeConfirmModal: closeConfirmModal,
        cancelConfirmModal: cancelConfirmModal,
        onBulkEditClick: onBulkEditClick,
        onEditFieldChange: onEditFieldChange,
        searchChargeWaiverApprovalData: searchChargeWaiverApprovalData,
        onSubmit: onSubmit,
        onApprove: onApprove,
        onReverse: onReverse,
        onGroupButtonClick: onGroupButtonClick,
        onReverseClick: onReverseClick,
        onRejectClick: onRejectClick,
        onReasonCloseClick: onReasonCloseClick,
        setIsSearchLoading: setIsSearchLoading,
        onSaveClicked: onSaveClicked,
    }
}